import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { clearDrawingPdfCache } from '../services/drawingPdfCache';
import { clearDrawingSetReview } from '../services/drawingSetReviewStore';
import { clearManagedDrawingOfflineData } from '../services/drawingOfflineStore';
import {
  canUseDrawingOffline,
  clearDrawingOfflineSession,
  readDrawingOfflineSession,
  saveDrawingOfflineSession,
} from '../services/drawingOfflineSession';
import { User } from '../types';
import { isPermissionPreview, closePermissionPreview } from '../services/permissionPreviewSession';

export type AuthErrorInfo = {
  message: string;
  retryAfterSeconds?: number;
  isNetworkError?: boolean;
  isTimeout?: boolean;
  // Account-lockout extras (status 423 from /auth/login)
  isLockedOut?: boolean;
  lockoutMinutesRemaining?: number;
  lockoutLevel?: number;
  lockoutContactMessage?: string;
  lockoutContactEmail?: string;
  lockoutContactPhone?: string;
};

export type LoginResult =
  | { success: true }
  | { success: false; error: AuthErrorInfo };

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<AuthErrorInfo | null>(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const hasBootstrappedRef = useRef(false);
  const bootstrapPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    bootstrapSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function bootstrapSession() {
    if (bootstrapPromiseRef.current) {
      return bootstrapPromiseRef.current;
    }

    bootstrapPromiseRef.current = (async () => {
    if (isPermissionPreview()) {
      try { setUser(await api.getMe({ silent: true, skipRefresh: true })); setError(null); }
      catch { setUser(null); setError('La vista previa expiró o ya no está disponible. Ciérrala y ábrela nuevamente desde permisos.'); }
      finally { setLoading(false); bootstrapPromiseRef.current = null; }
      return;
    }
    // When the browser already knows it is offline, use the short-lived,
    // permission-scoped local lease immediately. Waiting for /me and then a
    // refresh made an offline launch look frozen for up to two HTTP timeouts.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const offlineUser = await readDrawingOfflineSession().catch(() => null);
      if (offlineUser) {
        setError(null);
        setUser(offlineUser);
        setOfflineMode(true);
        setLoading(false);
        bootstrapPromiseRef.current = null;
        return;
      }
    }
    let initialError: any = null;
    try {
      setError(null);
      const userData = await api.getMe({ silent: true, skipRefresh: true });
      setUser(userData);
      setOfflineMode(false);
      if (canUseDrawingOffline(userData)) {
        void saveDrawingOfflineSession(userData).catch(() => {});
      } else {
        void clearDrawingOfflineSession().catch(() => {});
        void clearManagedDrawingOfflineData(userData.id).catch(() => {});
      }
    } catch (getMeError: any) {
      initialError = getMeError;
      try {
        await api.refreshSession();
        const refreshedUser = await api.getMe({ silent: true, skipRefresh: true });
        setUser(refreshedUser);
        setOfflineMode(false);
        if (canUseDrawingOffline(refreshedUser)) {
          void saveDrawingOfflineSession(refreshedUser).catch(() => {});
        } else {
          void clearDrawingOfflineSession().catch(() => {});
          void clearManagedDrawingOfflineData(refreshedUser.id).catch(() => {});
        }
      } catch (refreshErr: any) {
        const initialStatus = Number(initialError?.response?.status || 0);
        const refreshStatus = Number(refreshErr?.response?.status || 0);
        const initialUnavailable = !initialError?.response || initialStatus >= 500;
        const refreshUnavailable = !refreshErr?.response || refreshStatus >= 500;
        const networkUnavailable = initialUnavailable && refreshUnavailable;
        if (networkUnavailable) {
          const offlineUser = await readDrawingOfflineSession().catch(() => null);
          if (offlineUser) {
            setUser(offlineUser);
            setOfflineMode(true);
            return;
          }
        }
        api.clearAccessToken();
        void clearDrawingPdfCache().catch(() => {});
        void clearDrawingSetReview().catch(() => {});
        void clearDrawingOfflineSession().catch(() => {});
        void clearManagedDrawingOfflineData().catch(() => {});
        setUser(null);
        setOfflineMode(false);
        // If refresh was rejected by the server (4xx — CSRF missing, token invalid,
        // or session revoked), force a clean redirect to login so the page never
        // renders in a half-authenticated state.
        const status = refreshErr?.response?.status;
        if (status && status >= 400 && status < 500 && typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login?reason=session-expired';
        }
      }
    } finally {
      setLoading(false);
      bootstrapPromiseRef.current = null;
    }
    })();

    return bootstrapPromiseRef.current;
  }

  async function fetchUser() {
    if (isPermissionPreview()) { setUser(await api.getMe({ silent: true, skipRefresh: true })); return; }
    try {
      const userData = await api.getMe();
      setUser(userData);
      setOfflineMode(false);
      if (canUseDrawingOffline(userData)) {
        void saveDrawingOfflineSession(userData).catch(() => {});
      } else {
        void clearDrawingOfflineSession().catch(() => {});
        void clearManagedDrawingOfflineData(userData.id).catch(() => {});
      }
    } catch (err: any) {
      console.error('Failed to fetch user:', err);
      if ((!err?.response || Number(err?.response?.status || 0) >= 500) && user) {
        setOfflineMode(true);
        throw err;
      }
      api.clearAccessToken();
      void clearDrawingPdfCache().catch(() => {});
      void clearDrawingSetReview().catch(() => {});
      void clearDrawingOfflineSession().catch(() => {});
      void clearManagedDrawingOfflineData().catch(() => {});
      setUser(null);
      setOfflineMode(false);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string) {
    if (isPermissionPreview()) return { success: false, error: { message: 'Permission preview is read-only' } } as const;
    try {
      setError(null);
      setErrorInfo(null);
      const response = await api.login(email, password);
      setUser(response.user);
      setOfflineMode(false);
      if (canUseDrawingOffline(response.user)) {
        void saveDrawingOfflineSession(response.user).catch(() => {});
      } else {
        void clearDrawingOfflineSession().catch(() => {});
        void clearManagedDrawingOfflineData(response.user.id).catch(() => {});
      }
      return { success: true } as const;
    } catch (err: any) {
      const retryAfterHeader = err.response?.headers?.['retry-after'];
      const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : undefined;
      const isTimeout = err.code === 'ECONNABORTED';
      const isNetworkError = !err.response;
      const responseData = err.response?.data || {};
      const isLockedOut = err.response?.status === 423;
      const message =
        responseData.error ||
        (isTimeout
          ? 'The server is taking longer than usual. Please try again.'
          : isNetworkError
          ? 'Could not reach the server. Please check your connection and try again.'
          : 'Login failed');
      setError(message);
      const nextErrorInfo: AuthErrorInfo = {
        message,
        retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
        isNetworkError,
        isTimeout,
        isLockedOut,
        lockoutMinutesRemaining: isLockedOut ? Number(responseData.minutes_remaining) || undefined : undefined,
        lockoutLevel: isLockedOut ? Number(responseData.lockout_level) || undefined : undefined,
        lockoutContactMessage: isLockedOut ? responseData.contact_message : undefined,
        lockoutContactEmail: isLockedOut ? responseData.contact_email : undefined,
        lockoutContactPhone: isLockedOut ? responseData.contact_phone : undefined,
      };
      setErrorInfo(nextErrorInfo);
      return { success: false, error: nextErrorInfo } as const;
    }
  }

  // Stable identity: it only touches module-level helpers and state setters,
  // so consumers (e.g. the inactivity-logout listeners) are not re-subscribed
  // on every auth render.
  const logout = useCallback(async () => {
    if (isPermissionPreview()) { closePermissionPreview(); return; }
    try {
      await api.logout();
    } catch {
      // best effort
    } finally {
      try {
        api.clearAccessToken();
        await clearDrawingPdfCache().catch(() => {});
        await clearDrawingSetReview().catch(() => {});
        await clearDrawingOfflineSession().catch(() => {});
        await clearManagedDrawingOfflineData().catch(() => {});
        if (typeof window !== 'undefined') {
          for (const storageName of ['sessionStorage', 'localStorage'] as const) {
            try {
              const storage = window[storageName];
              Object.keys(storage)
                .filter((key) => key.startsWith('timesheetDraft:') || key === 'forxa.forecast.manualDraft.v2')
                .forEach((key) => { try { storage.removeItem(key); } catch { /* Best effort. */ } });
            } catch { /* Storage access or enumeration may be blocked. */ }
          }
        }
      } finally {
        setUser(null);
        setOfflineMode(false);
      }
    }
  }, []);

  return { user, loading, offlineMode, error, errorInfo, login, logout, fetchUser, bootstrapSession };
}
